import { useState } from "react";
import { 
  FileText, Gavel, Search, Users, UserCheck, DollarSign, 
  BookOpen, MapPin, Award, Shield, Loader2, CheckCircle2,
  AlertTriangle, ExternalLink, Mail, Phone, Building, ChevronRight
} from "lucide-react";
import { VerificationPanel, VerificationBadge, ConfidenceIndicator } from "@/components/shared/VerificationPanel";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const API = process.env.REACT_APP_BACKEND_URL;

const REQUEST_ICONS = {
  contract_review: FileText,
  court_expert: Gavel,
  investigator: Search,
  opposing_counsel: Users,
  witness_background: UserCheck,
  settlement_valuation: DollarSign,
  legal_precedent: BookOpen,
  jurisdiction_research: MapPin,
  expert_witness: Award,
  insurance_coverage: Shield
};

const CONFIDENCE_COLORS = {
  high: { bg: '#DCFCE7', text: '#166534', border: '#86EFAC' },
  medium: { bg: '#FEF3C7', text: '#92400E', border: '#FCD34D' },
  low: { bg: '#FEE2E2', text: '#991B1B', border: '#FCA5A5' }
};

const LegalRequestCenter = () => {
  const [requestTypes, setRequestTypes] = useState([]);
  const [selectedType, setSelectedType] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingTypes, setLoadingTypes] = useState(true);
  const [result, setResult] = useState(null);
  const [verificationStatus, setVerificationStatus] = useState("ai_generated");
  const [error, setError] = useState(null);
  
  // Form state
  const [subject, setSubject] = useState("");
  const [context, setContext] = useState("");
  const [jurisdiction, setJurisdiction] = useState("");
  const [caseNumber, setCaseNumber] = useState("");
  const [urgency, setUrgency] = useState("standard");

  // Load request types on mount
  useState(() => {
    const loadTypes = async () => {
      try {
        const response = await fetch(`${API}/api/requests/types`);
        const data = await response.json();
        setRequestTypes(data.types || []);
      } catch (err) {
        console.error("Failed to load request types:", err);
      } finally {
        setLoadingTypes(false);
      }
    };
    loadTypes();
  }, []);

  const submitRequest = async () => {
    if (!selectedType || !subject.trim()) {
      setError("Please select a request type and enter a subject");
      return;
    }

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const response = await fetch(`${API}/api/requests/submit`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          request_type: selectedType,
          subject: subject.trim(),
          context: context.trim() || null,
          jurisdiction: jurisdiction || null,
          case_number: caseNumber || null,
          urgency
        })
      });

      if (!response.ok) {
        throw new Error("Request failed");
      }

      const data = await response.json();
      setResult(data);
    } catch (err) {
      setError("Failed to process request. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setSelectedType(null);
    setSubject("");
    setContext("");
    setJurisdiction("");
    setCaseNumber("");
    setUrgency("standard");
    setResult(null);
    setError(null);
  };

  const selectedTypeInfo = requestTypes.find(t => t.id === selectedType);
  const TypeIcon = selectedType ? REQUEST_ICONS[selectedType] || Search : Search;

  return (
    <div className="space-y-6" data-testid="legal-request-center">
      {/* Header */}
      <div className="rounded-3xl p-6" style={{ background: 'linear-gradient(135deg, #1D4ED8, #3B82F6)', boxShadow: '0 20px 40px rgba(29,78,216,.25)' }}>
        <h1 className="text-2xl font-bold text-white mb-2">Legal Request Center</h1>
        <p className="text-blue-100">
          Submit research requests and receive comprehensive intelligence from multiple verified sources
        </p>
      </div>

      {!result ? (
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Request Type Selection */}
          <div className="lg:col-span-1">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Request Type</CardTitle>
                <CardDescription>Select the type of intelligence you need</CardDescription>
              </CardHeader>
              <CardContent className="space-y-2 max-h-[500px] overflow-y-auto">
                {requestTypes.map(type => {
                  const Icon = REQUEST_ICONS[type.id] || Search;
                  const isSelected = selectedType === type.id;
                  return (
                    <button
                      key={type.id}
                      onClick={() => setSelectedType(type.id)}
                      className={`w-full text-left p-3 rounded-xl border-2 transition-all ${
                        isSelected 
                          ? 'border-blue-500 bg-blue-50' 
                          : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                      }`}
                      data-testid={`request-type-${type.id}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                          isSelected ? 'bg-blue-500 text-white' : 'bg-slate-100 text-slate-600'
                        }`}>
                          <Icon className="h-5 w-5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className={`font-medium text-sm ${isSelected ? 'text-blue-700' : 'text-slate-800'}`}>
                            {type.label}
                          </div>
                          <div className="text-xs text-slate-500 truncate">{type.description}</div>
                        </div>
                        {isSelected && <CheckCircle2 className="h-5 w-5 text-blue-500 flex-shrink-0" />}
                      </div>
                    </button>
                  );
                })}
              </CardContent>
            </Card>
          </div>

          {/* Request Form */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TypeIcon className="h-5 w-5 text-blue-600" />
                  {selectedTypeInfo?.label || "Submit Request"}
                </CardTitle>
                <CardDescription>
                  {selectedTypeInfo?.description || "Select a request type to begin"}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {selectedTypeInfo && (
                  <div className="p-3 rounded-lg bg-blue-50 border border-blue-100">
                    <div className="text-xs font-semibold text-blue-600 mb-2">SOURCES CONSULTED</div>
                    <div className="flex flex-wrap gap-2">
                      {selectedTypeInfo.sources.map(source => (
                        <Badge key={source} variant="outline" className="text-xs bg-white">
                          {source}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                <div className="space-y-4">
                  <div>
                    <Label htmlFor="subject" className="text-slate-700">
                      Research Subject <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="subject"
                      placeholder="Enter person name, company, or topic to research..."
                      value={subject}
                      onChange={(e) => setSubject(e.target.value)}
                      className="mt-1"
                      data-testid="subject-input"
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      For individuals or companies, contact info will be included when available
                    </p>
                  </div>

                  <div>
                    <Label htmlFor="context" className="text-slate-700">Additional Context</Label>
                    <Textarea
                      id="context"
                      placeholder="Provide any relevant background information, specific questions, or areas of focus..."
                      value={context}
                      onChange={(e) => setContext(e.target.value)}
                      className="mt-1"
                      rows={3}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="jurisdiction" className="text-slate-700">Jurisdiction</Label>
                      <Select value={jurisdiction} onValueChange={setJurisdiction}>
                        <SelectTrigger className="mt-1">
                          <SelectValue placeholder="Select jurisdiction..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="federal">Federal</SelectItem>
                          <SelectItem value="california">California</SelectItem>
                          <SelectItem value="new_york">New York</SelectItem>
                          <SelectItem value="texas">Texas</SelectItem>
                          <SelectItem value="florida">Florida</SelectItem>
                          <SelectItem value="illinois">Illinois</SelectItem>
                          <SelectItem value="other">Other State</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label htmlFor="urgency" className="text-slate-700">Urgency</Label>
                      <Select value={urgency} onValueChange={setUrgency}>
                        <SelectTrigger className="mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="standard">Standard</SelectItem>
                          <SelectItem value="urgent">Urgent</SelectItem>
                          <SelectItem value="critical">Critical</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="caseNumber" className="text-slate-700">Case Number (Optional)</Label>
                    <Input
                      id="caseNumber"
                      placeholder="e.g., 2026-CV-00123"
                      value={caseNumber}
                      onChange={(e) => setCaseNumber(e.target.value)}
                      className="mt-1"
                    />
                  </div>
                </div>

                {error && (
                  <Alert variant="destructive">
                    <AlertTriangle className="h-4 w-4" />
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}

                <Button
                  onClick={submitRequest}
                  disabled={!selectedType || !subject.trim() || loading}
                  className="w-full h-12 rounded-xl font-bold text-white"
                  style={{ background: 'linear-gradient(135deg, #1D4ED8, #3B82F6)' }}
                  data-testid="submit-request-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                      Gathering Intelligence...
                    </>
                  ) : (
                    <>
                      Submit Request
                      <ChevronRight className="ml-2 h-5 w-5" />
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      ) : (
        /* Results View */
        <div className="space-y-6">
          {/* Result Header */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <TypeIcon className="h-6 w-6 text-blue-600" />
                    <h2 className="text-xl font-bold text-slate-800">{selectedTypeInfo?.label}</h2>
                    <Badge className="bg-emerald-100 text-emerald-700">Complete</Badge>
                  </div>
                  <p className="text-slate-600">Subject: <strong>{result.subject}</strong></p>
                </div>
                <Button variant="outline" onClick={resetForm}>
                  New Request
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Summary Box */}
          <Card className="border-2 border-blue-200">
            <CardHeader className="bg-blue-50">
              <CardTitle className="text-lg text-blue-800">Executive Summary</CardTitle>
            </CardHeader>
            <CardContent className="pt-4">
              <p className="text-slate-700 leading-relaxed">{result.summary}</p>
              
              {result.risk_factors && result.risk_factors.length > 0 && (
                <div className="mt-4 p-3 rounded-lg bg-amber-50 border border-amber-200">
                  <div className="flex items-center gap-2 text-amber-700 font-semibold mb-2">
                    <AlertTriangle className="h-4 w-4" />
                    Risk Factors Identified
                  </div>
                  <ul className="space-y-1">
                    {result.risk_factors.map((risk, i) => (
                      <li key={i} className="text-sm text-amber-800 flex items-start gap-2">
                        <span className="text-amber-500 mt-1">•</span>
                        {risk}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Contact Info (if available) */}
          {result.contact_info && (
            <Card className="border-2 border-emerald-200">
              <CardHeader className="bg-emerald-50">
                <CardTitle className="text-lg text-emerald-800 flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  Contact Information
                  {result.contact_info.verified && (
                    <Badge className="bg-emerald-100 text-emerald-700">Verified</Badge>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-3">
                    <div>
                      <p className="text-lg font-bold text-slate-800">{result.contact_info.name}</p>
                      {result.contact_info.title && (
                        <p className="text-slate-600">{result.contact_info.title}</p>
                      )}
                    </div>
                    {result.contact_info.organization && (
                      <div className="flex items-center gap-2 text-slate-600">
                        <Building className="h-4 w-4" />
                        {result.contact_info.organization}
                      </div>
                    )}
                    {result.contact_info.bar_number && (
                      <div className="text-sm">
                        <span className="text-slate-500">Bar #:</span>{" "}
                        <span className="font-medium">{result.contact_info.bar_number}</span>
                      </div>
                    )}
                  </div>
                  <div className="space-y-3">
                    {result.contact_info.email && (
                      <a href={`mailto:${result.contact_info.email}`} className="flex items-center gap-2 text-blue-600 hover:underline">
                        <Mail className="h-4 w-4" />
                        {result.contact_info.email}
                      </a>
                    )}
                    {result.contact_info.phone && (
                      <a href={`tel:${result.contact_info.phone}`} className="flex items-center gap-2 text-blue-600 hover:underline">
                        <Phone className="h-4 w-4" />
                        {result.contact_info.phone}
                      </a>
                    )}
                    {result.contact_info.linkedin && (
                      <a href={result.contact_info.linkedin} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-blue-600 hover:underline">
                        <ExternalLink className="h-4 w-4" />
                        LinkedIn Profile
                      </a>
                    )}
                    {result.contact_info.address && (
                      <p className="text-sm text-slate-600">{result.contact_info.address}</p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Source Answers */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Intelligence Sources ({result.answers.length})</CardTitle>
              <CardDescription>Findings from multiple verified databases</CardDescription>
            </CardHeader>
            <CardContent>
              <Tabs defaultValue={result.answers[0]?.source.toLowerCase().replace(/\s+/g, '_')} className="w-full">
                <TabsList className="w-full flex-wrap h-auto gap-1 bg-slate-100 p-1 rounded-xl">
                  {result.answers.map((answer, i) => (
                    <TabsTrigger 
                      key={i} 
                      value={answer.source.toLowerCase().replace(/\s+/g, '_')}
                      className="rounded-lg text-xs px-3 py-2"
                    >
                      {answer.source}
                    </TabsTrigger>
                  ))}
                </TabsList>
                
                {result.answers.map((answer, i) => (
                  <TabsContent 
                    key={i} 
                    value={answer.source.toLowerCase().replace(/\s+/g, '_')}
                    className="mt-4"
                  >
                    <div className="space-y-4">
                      <div className="flex items-center gap-3">
                        <Badge 
                          style={{ 
                            background: CONFIDENCE_COLORS[answer.confidence]?.bg,
                            color: CONFIDENCE_COLORS[answer.confidence]?.text,
                            border: `1px solid ${CONFIDENCE_COLORS[answer.confidence]?.border}`
                          }}
                        >
                          {answer.confidence.toUpperCase()} CONFIDENCE
                        </Badge>
                      </div>
                      
                      <div className="p-4 rounded-xl bg-slate-50 border">
                        <div className="font-semibold text-slate-800 mb-2">Key Insight</div>
                        <p className="text-slate-700">{answer.key_insight}</p>
                      </div>
                      
                      <div>
                        <div className="font-semibold text-slate-800 mb-2">Findings</div>
                        <ul className="space-y-2">
                          {answer.findings.map((finding, fi) => (
                            <li key={fi} className="flex items-start gap-2 text-slate-700">
                              <CheckCircle2 className="h-4 w-4 text-emerald-500 mt-1 flex-shrink-0" />
                              {finding}
                            </li>
                          ))}
                        </ul>
                      </div>
                      
                      {answer.citations && answer.citations.length > 0 && (
                        <div>
                          <div className="font-semibold text-slate-800 mb-2">Citations</div>
                          <ul className="space-y-1">
                            {answer.citations.map((citation, ci) => (
                              <li key={ci} className="text-sm text-slate-600 italic">{citation}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      
                      {answer.relevant_dates && answer.relevant_dates.length > 0 && (
                        <div>
                          <div className="font-semibold text-slate-800 mb-2">Relevant Dates</div>
                          <div className="flex flex-wrap gap-2">
                            {answer.relevant_dates.map((date, di) => (
                              <Badge key={di} variant="outline">{date}</Badge>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </TabsContent>
                ))}
              </Tabs>
            </CardContent>
          </Card>

          {/* Recommended Actions */}
          <Card className="border-2 border-slate-200">
            <CardHeader>
              <CardTitle className="text-lg">Recommended Actions</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-3">
                {result.recommended_actions.map((action, i) => (
                  <div key={i} className="flex items-center gap-3 p-3 rounded-xl bg-slate-50 border">
                    <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center font-bold text-sm">
                      {i + 1}
                    </div>
                    <span className="text-slate-700">{action}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
          
          {/* Lawyer Verification */}
          <div className="mt-6">
            <VerificationPanel
              itemId={result.request_id || result.id || "legal-request"}
              itemType="analysis"
              currentStatus={verificationStatus}
              onStatusChange={setVerificationStatus}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default LegalRequestCenter;
